import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { addDays, format, isBefore, startOfDay, startOfISOWeek } from 'date-fns';
import { nl } from 'date-fns/locale';

export interface CopyEventsDialogData {
  mode: 'day' | 'week';
  /** Pre-fills the source picker. Defaults to the currently displayed calendar date. */
  defaultSourceDate: Date;
}

export interface CopyEventsDialogResult {
  /** Start of the source day (day mode) or Monday of the source week (week mode). */
  source: Date;
  /** Start of the target day (day mode) or Monday of the target week (week mode). */
  target: Date;
}

@Component({
  selector: 'app-copy-events-dialog',
  imports: [
    MatButton,
    MatIcon,
    MatDatepickerModule,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
  ],
  templateUrl: './copy-events-dialog.component.html',
  styleUrl: './copy-events-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: MAT_DATE_FORMATS,
      useFactory: (data: CopyEventsDialogData) => ({
        parse: { dateInput: 'LL' },
        display: {
          dateInput: data.mode === 'week' ? "'Week' I, RRRR" : 'eeee d MMMM yyyy',
          dateA11yLabel: 'LL',
          monthYearLabel: 'MMM yyyy',
          monthYearA11yLabel: 'MMMM yyyy',
        },
      }),
      deps: [MAT_DIALOG_DATA],
    },
  ],
})
export class CopyEventsDialogComponent {
  protected readonly data = inject<CopyEventsDialogData>(MAT_DIALOG_DATA);
  private readonly _dialogRef = inject(MatDialogRef<CopyEventsDialogComponent>);
  private readonly _fb = inject(NonNullableFormBuilder);

  protected readonly today = startOfDay(new Date());

  /** Source date — no past restriction, any date can be a copy source. */
  protected readonly sourceControl = this._fb.control<Date | null>(
    this.data.defaultSourceDate,
    { validators: [Validators.required] }
  );

  /** Target date — must be in the future and different from the source period. */
  protected readonly targetControl = this._fb.control<Date | null>(null, {
    validators: [Validators.required],
  });

  private readonly _selectedSource = toSignal(this.sourceControl.valueChanges, {
    initialValue: this.data.defaultSourceDate,
  });

  private readonly _selectedTarget = toSignal(this.targetControl.valueChanges, {
    initialValue: null,
  });

  /**
   * Target filter: reactive to whatever the user picks as source.
   * Blocks the past and any date within the same source day/week.
   * Returned as a computed signal so the template can call it as targetFilter().
   */
  protected readonly targetFilter = computed(() => {
    const source = this._selectedSource();

    return (date: Date | null): boolean => {
      if (!date) return false;
      if (isBefore(date, this.today)) return false;

      if (!source) return true;

      if (this.data.mode === 'day') {
        return format(date, 'yyyy-MM-dd') !== format(source, 'yyyy-MM-dd');
      }

      // Week mode: exclude all 7 days of the source week
      const sourceWeekStart = startOfISOWeek(source);
      const sourceWeekEnd = addDays(sourceWeekStart, 7); // exclusive
      return date < sourceWeekStart || date >= sourceWeekEnd;
    };
  });

  /** Week-range label derived from the source picker (week mode only). */
  protected readonly sourceWeekLabel = computed(() => {
    if (this.data.mode !== 'week') return null;
    const selected = this._selectedSource();
    if (!selected) return null;
    return this._weekLabel(selected);
  });

  /** Week-range label derived from the target picker (week mode only). */
  protected readonly targetWeekLabel = computed(() => {
    if (this.data.mode !== 'week') return null;
    const selected = this._selectedTarget();
    if (!selected) return null;
    return this._weekLabel(selected);
  });

  protected get canConfirm(): boolean {
    return this.sourceControl.valid && this.targetControl.valid;
  }

  confirm(): void {
    const source = this.sourceControl.value;
    const target = this.targetControl.value;
    if (!source || !target) return;

    const result: CopyEventsDialogResult =
      this.data.mode === 'week'
        ? { source: startOfISOWeek(source), target: startOfISOWeek(target) }
        : { source, target };

    this._dialogRef.close(result);
  }

  cancel(): void {
    this._dialogRef.close(null);
  }

  private _weekLabel(date: Date): string {
    const weekStart = startOfISOWeek(date);
    const weekEnd = addDays(weekStart, 6);
    return (
      `Week van maandag ${format(weekStart, 'd MMMM', { locale: nl })}` +
      ` t/m zondag ${format(weekEnd, 'd MMMM yyyy', { locale: nl })}`
    );
  }
}
